import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { toPromoBrand } from '~/consts/promoBrand';

import { useGetMyVouchersCount } from '~/hooks/apis/promo/useGetMyVouchers';

import isUUID from '~/utils/isUUID';

import DebugModal from '../DebugModal/DebugModal';

type Props = {
  brand: string;
  userId: string;
  region: string;
};

export default function VoucherCountPreview({ brand, userId, region }: Props) {
  const [countUserId, setCountUserId] = useState(userId);
  const [reportingTag, setReportingTag] = useState('');
  const [cartId, setCartId] = useState('');
  const normalizedUserId = countUserId.trim();
  const invalidUserId = Boolean(normalizedUserId) && !isUUID(normalizedUserId);
  const promoBrand = toPromoBrand(brand);
  const normalizedCartId = cartId.trim();
  const invalidCartId = Boolean(normalizedCartId) && !isUUID(normalizedCartId);
  const { data, error, isFetching, refetch } = useGetMyVouchersCount({
    brand: promoBrand,
    overrideUserId: normalizedUserId,
    region,
    voucherReportingTag: reportingTag.trim() || undefined,
    cartId: normalizedCartId || undefined,
  });

  useEffect(() => {
    setCountUserId(userId);
  }, [userId]);

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1">Customer voucher count</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Check the customer menu voucher count for this user. Optional cart context does not change the count.
          </Typography>
          <TextField
            required
            label="User ID"
            value={countUserId}
            onChange={(event) => setCountUserId(event.target.value)}
            error={invalidUserId}
            helperText={
              invalidUserId
                ? 'Enter a valid user UUID.'
                : 'Prefilled from the selected user. Enter another user ID to check their count.'
            }
          />
          {!promoBrand && <Alert severity="error">The selected tenant is not a supported promo brand.</Alert>}
          <Accordion variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Secondary parameters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Brand"
                  value={brand}
                  InputProps={{ readOnly: true }}
                  helperText="Uses the selected tenant."
                />
                <TextField
                  label="Region"
                  value={region}
                  InputProps={{ readOnly: true }}
                  helperText="Uses the selected region above."
                />
                <TextField
                  label="Voucher reporting tag"
                  value={reportingTag}
                  onChange={(event) => setReportingTag(event.target.value)}
                  helperText="Exact voucher tag match; leave empty to count all eligible vouchers."
                />
                <TextField
                  label="Cart ID"
                  value={cartId}
                  onChange={(event) => setCartId(event.target.value)}
                  error={invalidCartId}
                  helperText={invalidCartId ? 'Enter a valid cart UUID.' : 'Optional checkout cart transaction key.'}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Stack direction="row">
            <Button
              variant="contained"
              color="primary"
              disabled={!normalizedUserId || invalidUserId || !promoBrand || invalidCartId || isFetching}
              onClick={() => void refetch()}
            >
              {isFetching ? 'Loading…' : 'Load voucher count'}
            </Button>
          </Stack>
          {error && <Alert severity="error">{error.message}</Alert>}
          {data && (
            <Stack spacing={1}>
              <Typography variant="body1">Customer voucher count: {data.result.count}</Typography>
              {data.result.cart && (
                <Typography variant="body2">
                  Cart items: {data.result.cart.itemCount}
                  {data.result.cart.type ? ` (${data.result.cart.type})` : ''}
                </Typography>
              )}
              <DebugModal title="Voucher count response" type="generic" data={data} />
            </Stack>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
